import { useCurrentTemplateSet } from '@/composables/provider/useCurrentTemplateSet';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { useLastViewed } from '@/composables/useLastViewed';
import { usePermissions } from '@/composables/usePermissions';
import { useSchoolLevels } from '@/composables/useSchoolLevels';
import { watchEffect } from 'vue';

/**
 * School levels for the CURRENT context. A provider editing a template set gets the levels + label mode from that
 * set's resolved SchoolTypeConfiguration; a tenant uses its own configured values. (Field naming differs across the
 * two DTOs: `templateSet.labelMode` vs `tenant.schoolLevelLabelMode` — normalized here.) All presentation logic lives
 * in {@link useSchoolLevels}.
 */
export const useSchoolLevelsBySchoolType = () => {
  const { lastViewedSchoolLevelFilter, setLastViewedSchoolLevelFilter } = useLastViewed();
  const { currentTemplateSet } = useCurrentTemplateSet();
  const { hasProviderAccess } = usePermissions();
  const { currentTenant } = useCurrentTenant();

  const {
    schoolLevels,
    schoolLevelOptions,
    getSchoolLevelLabel,
    labelMode,
    useRawSchoolLevelLabels,
    lastSchoolLevel,
    hasNextSchoolLevel,
  } = useSchoolLevels(
    () => (hasProviderAccess.value ? currentTemplateSet.value?.schoolLevels : currentTenant.value?.schoolLevels) ?? [],
    () => (hasProviderAccess.value ? currentTemplateSet.value?.labelMode : currentTenant.value?.schoolLevelLabelMode),
  );

  watchEffect(() => {
    const validLevels = schoolLevelOptions.value.map((option) => option.value);
    if (!validLevels.includes(lastViewedSchoolLevelFilter.value)) {
      setLastViewedSchoolLevelFilter(validLevels[0] ?? 1);
    }
  });

  return {
    schoolLevels,
    schoolLevelOptions,
    getSchoolLevelLabel,
    labelMode,
    useRawSchoolLevelLabels,
    lastSchoolLevelForCurrentSchool: lastSchoolLevel,
    hasNextSchoolLevel,
  };
};
